import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff, 
  Shield, 
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
} from 'lucide-react';
import { getApiKeys, createApiKey, revokeApiKey } from '../../lib/api';

export default function ApiSettings() {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScope, setNewKeyScope] = useState('read');
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const res = await getApiKeys();
      setKeys(res.data);
    } catch {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      toast.error('Key name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await createApiKey({ name: newKeyName.trim(), scope: newKeyScope });
      setNewlyCreatedKey(res.data);
      setCreateDialogOpen(false);
      setNewKeyName('');
      setNewKeyScope('read');
      loadKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!keyToRevoke) return;
    try {
      await revokeApiKey(keyToRevoke.id);
      toast.success(`Key "${keyToRevoke.name}" revoked`);
      setRevokeDialogOpen(false);
      setKeyToRevoke(null);
      loadKeys();
    } catch {
      toast.error('Failed to revoke key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const scopeLabel = { read: 'Read Only', read_write: 'Read & Write', admin: 'Admin' };
  const scopeColor = { read: 'bg-blue-100 text-blue-700', read_write: 'bg-amber-100 text-amber-700', admin: 'bg-red-100 text-red-700' };

  const activeKeys = keys.filter(k => k.is_active);
  const revokedKeys = keys.filter(k => !k.is_active);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="w-6 h-6" /> API Settings
          </h1>
          <p className="text-sm text-muted-foreground">Manage API keys for programmatic access to your data</p>
        </div>
      </div>

      {/* Security Info */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-800 space-y-1">
              <p className="font-medium">API Key Authentication</p>
              <p>Use API keys to authenticate programmatic requests. Include the key as a Bearer token:</p>
              <code className="block bg-blue-100 rounded px-3 py-1.5 text-xs font-mono mt-1">
                Authorization: Bearer cbk_your_key_here
              </code>
              <p className="text-xs text-blue-600 mt-2">Keys are shown once at creation. Store them securely, they cannot be retrieved later.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Keys */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg">Active API Keys</CardTitle>
            <CardDescription>{activeKeys.length} active key{activeKeys.length !== 1 ? 's' : ''}</CardDescription>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="btn-create-key">
            <Plus className="w-4 h-4 mr-1" /> Create Key
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : activeKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No API keys created yet</p>
              <p className="text-xs mt-1">Create a key to enable programmatic access</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeKeys.map(key => (
                <div key={key.id} className="flex items-center justify-between p-3 border rounded-lg bg-white" data-testid={`api-key-${key.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{key.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${scopeColor[key.scope]}`}>
                        {scopeLabel[key.scope]}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="font-mono">{key.key_prefix}</span>
                      <span>Created by {key.created_by}</span>
                      <span>{new Date(key.created_at).toLocaleDateString()}</span>
                      {key.last_used_at ? (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last used {new Date(key.last_used_at).toLocaleDateString()} ({key.usage_count} calls)
                        </span>
                      ) : (
                        <span className="text-amber-500">Never used</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => { setKeyToRevoke(key); setRevokeDialogOpen(true); }}
                    data-testid={`btn-revoke-${key.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoked Keys */}
      {revokedKeys.length > 0 && (
        <Card className="opacity-60">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-muted-foreground">Revoked Keys</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {revokedKeys.map(key => (
                <div key={key.id} className="flex items-center justify-between p-2 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="line-through">{key.name}</span>
                    <span className="font-mono text-xs">{key.key_prefix}</span>
                    <span className="text-xs">Revoked {key.revoked_at ? new Date(key.revoked_at).toLocaleDateString() : ''} by {key.revoked_by}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scope Reference */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Scope Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded-lg border bg-blue-50/50">
              <p className="font-medium text-blue-700 mb-1">Read Only</p>
              <p className="text-xs text-muted-foreground">View products, customers, orders, reports. Cannot modify data.</p>
            </div>
            <div className="p-3 rounded-lg border bg-amber-50/50">
              <p className="font-medium text-amber-700 mb-1">Read & Write</p>
              <p className="text-xs text-muted-foreground">Full data access: create orders, update products, manage dispatches.</p>
            </div>
            <div className="p-3 rounded-lg border bg-red-50/50">
              <p className="font-medium text-red-700 mb-1">Admin</p>
              <p className="text-xs text-muted-foreground">Full admin access including settings, user management, and imports.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Create a new API key for programmatic access.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Key Name *</Label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. ERP Integration, Reporting Script"
                data-testid="input-key-name"
              />
            </div>
            <div>
              <Label>Access Scope</Label>
              <Select value={newKeyScope} onValueChange={setNewKeyScope}>
                <SelectTrigger data-testid="select-key-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Read Only, View data only</SelectItem>
                  <SelectItem value="read_write">Read & Write, Create and modify data</SelectItem>
                  <SelectItem value="admin">Admin, Full access including settings</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="btn-confirm-create">
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newly Created Key Dialog */}
      <Dialog open={!!newlyCreatedKey} onOpenChange={() => setNewlyCreatedKey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" /> API Key Created
            </DialogTitle>
            <DialogDescription>
              Copy this key now. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          {newlyCreatedKey && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs text-muted-foreground">Key Name</Label>
                <p className="font-medium">{newlyCreatedKey.name}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Scope</Label>
                <p>{scopeLabel[newlyCreatedKey.scope]}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">API Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    readOnly
                    value={showKey ? newlyCreatedKey.key : '••••••••••••••••••••••••••••••••'}
                    className="font-mono text-xs bg-muted"
                    data-testid="new-key-value"
                  />
                  <Button variant="outline" size="sm" onClick={() => setShowKey(!showKey)} data-testid="btn-toggle-key">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(newlyCreatedKey.key)} data-testid="btn-copy-key">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Store this key in a secure location. Once you close this dialog, the full key cannot be retrieved, only the prefix will be shown.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setNewlyCreatedKey(null); setShowKey(false); }} data-testid="btn-close-key-dialog">
              I've Saved the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Revoke API Key
            </DialogTitle>
            <DialogDescription>
              This will immediately disable the key. Any applications using it will lose access.
            </DialogDescription>
          </DialogHeader>
          {keyToRevoke && (
            <div className="py-2">
              <p className="text-sm"><strong>Key:</strong> {keyToRevoke.name}</p>
              <p className="text-sm font-mono text-muted-foreground">{keyToRevoke.key_prefix}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRevoke} data-testid="btn-confirm-revoke">
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
